'use client'

import React from "react"

import { useRef, useState, useEffect } from 'react'
import {
  FolderUp,
  FilePlus,
  FolderPlus,
  Play,
  Square,
  Trash2,
  Download,
  Zap,
  Github,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogClose,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import { FileTree } from './file-tree'
import { useFileStore } from '@/lib/file-store'
import { useWebContainer } from '@/hooks/use-webcontainer'
import { useWebContainerStore } from '@/lib/webcontainer-store'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { ThemeActivationModal } from './theme-activation-modal'
import { getAvailableThemes, getSiteRepos, pushThemeBuildToGithub } from '@/lib/api'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { useActiveThemeId } from '@/hooks/useActiveThemeId'
import consumer from '@/lib/cable'

const TEMPLATES = {
  'vanilla-html': {
    name: 'Vanilla HTML',
    files: [
      {
        path: 'index.html',
        content: `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>My Website</title>
  <link rel="stylesheet" href="styles.css">
</head>
<body>
  <main>
    <h1>Hello World!</h1>
    <p>Edit this file to get started.</p>
  </main>
  <script src="script.js"></script>
</body>
</html>`,
      },
      {
        path: 'styles.css',
        content: `* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: system-ui, -apple-system, sans-serif;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #e0e0e0;
}

main {
  text-align: center;
  padding: 2rem;
}

h1 {
  font-size: 3rem;
  margin-bottom: 1rem;
  background: linear-gradient(90deg, #60a5fa, #34d399);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

p {
  color: #a0a0a0;
  font-size: 1.25rem;
}`,
      },
      {
        path: 'script.js',
        content: `console.log('Hello from script.js!');

document.querySelector('h1').addEventListener('click', () => {
  alert('You clicked the heading!');
});`,
      },
    ],
  },
  'react-vite': {
    name: 'React + Vite',
    files: [
      {
        path: 'package.json',
        content: JSON.stringify(
          {
            name: 'vite-react-app',
            private: true,
            version: '0.0.0',
            type: 'module',
            scripts: {
              dev: 'vite',
              build: 'vite build',
              preview: 'vite preview',
            },
            dependencies: {
              react: '^18.2.0',
              'react-dom': '^18.2.0',
            },
            devDependencies: {
              '@vitejs/plugin-react': '^4.2.1',
              vite: '^5.0.0',
            },
          },
          null,
          2
        ),
      },
      {
        path: 'vite.config.js',
        content: `import { defineConfig } from 'vite'
import react from '@vitejs/plugin-react'

export default defineConfig({
  plugins: [react()],
})`,
      },
      {
        path: 'index.html',
        content: `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <title>Vite + React</title>
  </head>
  <body>
    <div id="root"></div>
    <script type="module" src="/src/main.jsx"></script>
  </body>
</html>`,
      },
      {
        path: 'src/main.jsx',
        content: `import React from 'react'
import ReactDOM from 'react-dom/client'
import App from './App'
import './index.css'

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
)`,
      },
      {
        path: 'src/App.jsx',
        content: `import { useState } from 'react'

function App() {
  const [count, setCount] = useState(0)

  return (
    <div className="app">
      <h1>Vite + React</h1>
      <div className="card">
        <button onClick={() => setCount((count) => count + 1)}>
          count is {count}
        </button>
      </div>
      <p>Edit <code>src/App.jsx</code> and save to test HMR</p>
    </div>
  )
}

export default App`,
      },
      {
        path: 'src/index.css',
        content: `:root {
  font-family: Inter, system-ui, sans-serif;
  line-height: 1.5;
  font-weight: 400;
  color: #ffffffde;
  background-color: #242424;
}

body {
  margin: 0;
  min-width: 320px;
  min-height: 100vh;
  display: flex;
  place-items: center;
}

.app {
  max-width: 1280px;
  margin: 0 auto;
  padding: 2rem;
  text-align: center;
}

h1 {
  font-size: 3.2em;
  line-height: 1.1;
}

button {
  border-radius: 8px;
  border: 1px solid transparent;
  padding: 0.6em 1.2em;
  font-size: 1em;
  font-family: inherit;
  background-color: #1a1a1a;
  color: #fff;
  cursor: pointer;
  transition: border-color 0.25s;
}

button:hover {
  border-color: #646cff;
}

.card {
  padding: 2em;
}`,
      },
    ],
  },
}

export function IDESidebar() {
  const { addFile, addFolder, setFiles, files, importFiles } = useFileStore()
  const { installAndRun, isBooting } = useWebContainer()
  const { currentProcess } = useWebContainerStore()
  const folderInputRef = useRef<HTMLInputElement>(null)
  const [newFileName, setNewFileName] = useState('')
  const [newFolderName, setNewFolderName] = useState('')
  const [isFileDialogOpen, setIsFileDialogOpen] = useState(false)
  const [isFolderDialogOpen, setIsFolderDialogOpen] = useState(false)
  const [isThemeModalOpen, setIsThemeModalOpen] = useState(false)
  const [selectedTheme, setSelectedTheme] = useState<any>(null)
  const [availableThemes, setAvailableThemes] = useState<{
    system_themes: any[]
    site_themes: any[]
  } | null>(null)
  const [isGithubDialogOpen, setIsGithubDialogOpen] = useState(false)
  const [githubRepos, setGithubRepos] = useState<Array<{ label: string; value: string }>>([])
  const [selectedRepo, setSelectedRepo] = useState('')
  const [branchName, setBranchName] = useState('')
  const [commitMessage, setCommitMessage] = useState('DevEditor update')
  const [isPushing, setIsPushing] = useState(false)
  const [pushStatus, setPushStatus] = useState<'idle' | 'processing' | 'complete' | 'error'>('idle')
  const [pushMessages, setPushMessages] = useState<string[]>([])
  const [pushResult, setPushResult] = useState<{ repo?: string; branch?: string; commit?: string } | null>(null)
  const { siteSlug, themeBuildId } = useParams<{ siteSlug: string; themeBuildId?: string }>()
  const { themeId } = useActiveThemeId(siteSlug)

  // Fetch available themes on mount
  useEffect(() => {
    if (siteSlug) {
      getAvailableThemes(siteSlug)
        .then((data) => {
          setAvailableThemes(data)
        })
        .catch((error) => {
          console.error('Failed to fetch themes:', error)
          toast.error('Failed to load available themes')
        })
    }
  }, [siteSlug])

  const handleFolderUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files
    if (fileList && fileList.length > 0) {
      await importFiles(fileList)
    }
    if (e.target) e.target.value = ''
  }

  const handleCreateFile = () => {
    if (newFileName.trim()) {
      addFile(newFileName.trim())
      setNewFileName('')
      setIsFileDialogOpen(false)
    }
  }

  const handleCreateFolder = () => {
    if (newFolderName.trim()) {
      addFolder(newFolderName.trim())
      setNewFolderName('')
      setIsFolderDialogOpen(false)
    }
  }

  const handleLoadTemplate = (templateKey: keyof typeof TEMPLATES) => {
    const template = TEMPLATES[templateKey]
    setFiles([])
    template.files.forEach(({ path, content }) => {
      addFile(path, content)
    })
  }

  const handleLoadTheme = (theme: any) => {
    setSelectedTheme(theme)
    setIsThemeModalOpen(true)
  }

  const handleClearAll = () => {
    setFiles([])
  }

  const handleExport = () => {
    // Simple JSON export of files
    const data = JSON.stringify(files, null, 2)
    const blob = new Blob([data], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'project.json'
    a.click()
    URL.revokeObjectURL(url)
  }

  const handleOpenGithubDialog = async () => {
    if (!siteSlug) return
    setIsGithubDialogOpen(true)
    setPushStatus('idle')
    setPushMessages([])
    setPushResult(null)
    try {
      const repos = await getSiteRepos(siteSlug)
      const normalized = repos.map((repo: any) => ({
        label: Array.isArray(repo) ? repo[0] : repo,
        value: Array.isArray(repo) ? repo[0] : repo
      }))
      setGithubRepos(normalized)
      if (normalized.length > 0 && !selectedRepo) {
        setSelectedRepo(normalized[0].value)
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to load GitHub repos')
    }
  }

  const handlePushToGithub = async () => {
    if (!siteSlug || !themeId || !themeBuildId) {
      toast.error('Missing site or build context')
      return
    }
    if (!selectedRepo) {
      toast.error('Select a repository first')
      return
    }
    setIsPushing(true)
    setPushStatus('processing')
    setPushMessages(['Starting GitHub push...'])
    setPushResult(null)
    try {
      await pushThemeBuildToGithub(siteSlug, themeId, themeBuildId, {
        repo: selectedRepo,
        branch_name: branchName || undefined,
        commit_message: commitMessage || undefined
      })
    } catch (error: any) {
      toast.error(error.message || 'Failed to push to GitHub')
      setPushStatus('error')
    } finally {
      setIsPushing(false)
    }
  }

  useEffect(() => {
    if (!isGithubDialogOpen || !themeBuildId) return

    const channel = consumer.subscriptions.create(
      {
        channel: 'GithubPushChannel',
        theme_build_id: themeBuildId
      },
      {
        received(data: any) {
          if (data.type === 'progress') {
            const suffix = data.current && data.total ? ` (${data.current}/${data.total})` : ''
            setPushMessages((prev) => [...prev, `${data.message}${suffix}`])
            return
          }

          if (data.type === 'status' && data.status === 'processing') {
            setPushMessages((prev) => [...prev, data.message])
            setPushStatus('processing')
            return
          }

          if (data.type === 'complete') {
            setPushStatus('complete')
            setPushResult({ repo: data.repo, branch: data.branch, commit: data.commit })
            setPushMessages((prev) => [...prev, 'Push complete'])
            return
          }

          if (data.type === 'error') {
            setPushStatus('error')
            setPushMessages((prev) => [...prev, `Error: ${data.message}`])
            return
          }
        }
      }
    )

    return () => {
      channel.unsubscribe()
    }
  }, [isGithubDialogOpen, themeBuildId])

  const isRunning = currentProcess !== null

  return (
    <TooltipProvider>
      <div className="h-full min-h-0 flex flex-col overflow-hidden bg-[color:var(--file-tree-bg)]">
        {/* Header */}
        <div className="p-3 border-b border-border">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-primary" />
              <span className="font-semibold text-foreground">WebStudio</span>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex flex-wrap gap-1">
            <Dialog open={isGithubDialogOpen} onOpenChange={setIsGithubDialogOpen}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <DialogTrigger asChild>
                    <Button
                      variant="secondary"
                      size="icon"
                      className="h-8 w-8"
                      onClick={handleOpenGithubDialog}
                    >
                      <Github className="h-4 w-4" />
                    </Button>
                  </DialogTrigger>
                </TooltipTrigger>
                <TooltipContent>Push to GitHub</TooltipContent>
              </Tooltip>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Push to GitHub</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-2">
                  <div className="space-y-2">
                    <Label htmlFor="githubRepo">Repository</Label>
                    <select
                      id="githubRepo"
                      value={selectedRepo}
                      onChange={(e) => setSelectedRepo(e.target.value)}
                      className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                    >
                      {githubRepos.map((repo) => (
                        <option key={repo.value} value={repo.value}>
                          {repo.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="githubBranch">Branch name (optional)</Label>
                    <Input
                      id="githubBranch"
                      placeholder="dev-editor-branch"
                      value={branchName}
                      onChange={(e) => setBranchName(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="githubCommit">Commit message</Label>
                    <Input
                      id="githubCommit"
                      value={commitMessage}
                      onChange={(e) => setCommitMessage(e.target.value)}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <DialogClose asChild>
                    <Button variant="outline" disabled={isPushing}>Cancel</Button>
                  </DialogClose>
                  <Button onClick={handlePushToGithub} disabled={isPushing || !selectedRepo}>
                    {isPushing ? 'Pushing...' : 'Push'}
                  </Button>
                </DialogFooter>
                {pushStatus !== 'idle' && (
                  <div className="mt-4 rounded-md border border-border bg-muted/20 p-3 text-xs text-muted-foreground space-y-1">
                    {pushMessages.map((message, idx) => (
                      <div key={idx}>{message}</div>
                    ))}
                    {pushStatus === 'complete' && pushResult && (
                      <div className="text-foreground">
                        Done: {pushResult.repo} ({pushResult.branch})
                      </div>
                    )}
                  </div>
                )}
              </DialogContent>
            </Dialog>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="secondary"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => folderInputRef.current?.click()}
                >
                  <FolderUp className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Upload Folder</TooltipContent>
            </Tooltip>
            <input
              ref={folderInputRef}
              type="file"
              className="hidden"
              // @ts-expect-error - webkitdirectory is not in the types
              webkitdirectory="true"
              directory="true"
              multiple
              onChange={handleFolderUpload}
            />

            <Dialog open={isFileDialogOpen} onOpenChange={setIsFileDialogOpen}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <DialogTrigger asChild>
                    <Button variant="secondary" size="icon" className="h-8 w-8">
                      <FilePlus className="h-4 w-4" />
                    </Button>
                  </DialogTrigger>
                </TooltipTrigger>
                <TooltipContent>New File</TooltipContent>
              </Tooltip>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create New File</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="fileName">File path</Label>
                    <Input
                      id="fileName"
                      placeholder="e.g. src/components/Button.tsx"
                      value={newFileName}
                      onChange={(e) => setNewFileName(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleCreateFile()}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <DialogClose asChild>
                    <Button variant="outline">Cancel</Button>
                  </DialogClose>
                  <Button onClick={handleCreateFile}>Create</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <Dialog open={isFolderDialogOpen} onOpenChange={setIsFolderDialogOpen}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <DialogTrigger asChild>
                    <Button variant="secondary" size="icon" className="h-8 w-8">
                      <FolderPlus className="h-4 w-4" />
                    </Button>
                  </DialogTrigger>
                </TooltipTrigger>
                <TooltipContent>New Folder</TooltipContent>
              </Tooltip>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create New Folder</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="folderName">Folder path</Label>
                    <Input
                      id="folderName"
                      placeholder="e.g. src/components"
                      value={newFolderName}
                      onChange={(e) => setNewFolderName(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <DialogClose asChild>
                    <Button variant="outline">Cancel</Button>
                  </DialogClose>
                  <Button onClick={handleCreateFolder}>Create</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="secondary"
                  size="icon"
                  className="h-8 w-8"
                  onClick={handleExport}
                  disabled={files.length === 0}
                >
                  <Download className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Export Project</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="secondary"
                  size="icon"
                  className="h-8 w-8"
                  onClick={handleClearAll}
                  disabled={files.length === 0}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Clear All</TooltipContent>
            </Tooltip>
          </div>
        </div>

        {/* Run button */}
        <div className="p-3 border-b border-border">
          <Button
            className="w-full"
            onClick={installAndRun}
            disabled={isBooting || files.length === 0}
          >
            {isRunning ? (
              <>
                <Square className="h-4 w-4 mr-2" />
                Running...
              </>
            ) : isBooting ? (
              'Booting...'
            ) : (
              <>
                <Play className="h-4 w-4 mr-2" />
                Run
              </>
            )}
          </Button>
        </div>

        {/* Templates */}
        <div className="p-3 border-b border-border flex flex-col min-h-0">
          <p className="text-xs text-muted-foreground mb-2">Quick Start Templates</p>
          <ScrollArea className="max-h-56">
            <div className="flex flex-col gap-1 pr-2">
            <Button
              variant="ghost"
              size="sm"
              className="justify-start text-xs h-7"
              onClick={() => handleLoadTemplate('vanilla-html')}
            >
              Vanilla HTML/CSS/JS
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="justify-start text-xs h-7"
              onClick={() => handleLoadTemplate('react-vite')}
            >
              React + Vite
            </Button>
            
            {/* Backend Themes */}
            {availableThemes?.system_themes && availableThemes.system_themes.length > 0 && (
              <>
                <Separator className="my-2" />
                <p className="text-xs text-muted-foreground mb-1">System Themes</p>
                {availableThemes.system_themes.map((theme) => (
                  <Button
                    key={theme.id}
                    variant="ghost"
                    size="sm"
                    className="justify-start text-xs h-7"
                    onClick={() => handleLoadTheme(theme)}
                  >
                    {theme.name}
                  </Button>
                ))}
              </>
            )}
            
            {availableThemes?.site_themes && availableThemes.site_themes.length > 0 && (
              <>
                <Separator className="my-2" />
                <p className="text-xs text-muted-foreground mb-1">Site Themes</p>
                {availableThemes.site_themes.map((theme) => (
                  <Button
                    key={theme.id}
                    variant="ghost"
                    size="sm"
                    className="justify-start text-xs h-7"
                    onClick={() => handleLoadTheme(theme)}
                  >
                    {theme.name}
                  </Button>
                ))}
              </>
            )}
            </div>
          </ScrollArea>
        </div>

        <Separator />

        {/* File tree */}
        <div className="p-2">
          <p className="text-xs text-muted-foreground px-2 mb-1">Files</p>
        </div>
        <ScrollArea className="flex-1 min-h-0">
          <FileTree />
        </ScrollArea>
        
        {/* Theme Activation Modal */}
        <ThemeActivationModal
          open={isThemeModalOpen}
          onOpenChange={setIsThemeModalOpen}
          theme={selectedTheme}
          siteId={siteSlug || ''}
          onActivationComplete={() => {
            toast.success('Theme activated! Ready to run.')
          }}
        />
      </div>
    </TooltipProvider>
  )
}
